/**
 * Build-time feature flags — FROZEN. Do not add to this file.
 *
 * "Toggle features on/off without code changes" was never true of this module.
 * Every entry below is a TypeScript literal: Vite inlines it at build time, so
 * turning one off costs a rebuild and a redeploy — and on the mobile side, a
 * new store binary. That is the opposite of what a flag is for.
 *
 * THE RUNTIME REGISTRY IS `packages/foundation/src/feature-flags` (W0.1 /
 * DW8-1). Declare a new flag there with `false`, read it through
 * `useFeatureFlags()` from `@tonnie/sdk`, and a platform admin can flip it for
 * a tenant, a user or globally without shipping anything — reaching open
 * sessions within the hook's five-minute staleTime.
 *
 *     import { useFeatureFlags } from '@tonnie/sdk';
 *     const { flags, isResolved } = useFeatureFlags();
 *     if (isResolved && flags.MY_NEW_FLAG) { … }
 *
 * `npm run qa:feature-flags` enforces the freeze: the keys below are pinned,
 * and a new one fails the build naming the registry.
 *
 * WHAT IS STILL HERE. Every entry is `false`: an unshipped feature, which
 * CAN move to the registry unchanged — and should, one feature at a time as
 * each is picked up. The `true` entries were not flags at all (permanently
 * enabled features whose `false` branch was unreachable); they were retired on
 * 4 Oct 2026 by deleting each flag and inlining its enabled branch.
 *
 * Usage (existing call sites only):
 *   import { featureFlags } from '@/core/config/feature-flags';
 *   if (featureFlags.ENABLE_2FA) { ... }
 */

export const featureFlags = {
  /**
   * Two-Factor Authentication (TOTP) via Clerk.
   *
   * Requires TOTP to be enabled in the Clerk Dashboard:
   *   Dashboard → User & Authentication → Multi-factor → Enable TOTP
   *
   * Set to `true` once Clerk TOTP is configured.
   */
  ENABLE_2FA: false,

  /**
   * The tenant's custom theme (the branding its admin saves on Business web).
   *
   * Controls:
   *   - "Custom" theme option in ThemeSelector dropdown
   *   - Light and Dark options are always visible regardless of this flag
   */
  ENABLE_CUSTOM_THEME: false,

  /**
   * Journey Details modal.
   *
   * Controls:
   *   - Info button on each journey card in the JourneyInstanceSelector
   *   - JourneyDetailsModal triggered by the info button
   */
  ENABLE_JOURNEY_DETAILS: false,

  /**
   * Care Support requests (R2).
   *
   * Controls:
   *   - "Support" tab on Care page
   *   - Support request creation and listing
   */
  ENABLE_CARE_SUPPORT: false,

  /**
   * Tonnie Checked verification badges.
   *
   * Powered by CoPilot AI document/data verification.
   * Controls:
   *   - "Tonnie Checked" badges across Vault, TelePortal, Passport, Career
   *   - When disabled, all Tonnie Checked badges are hidden
   */
  ENABLE_TONNIE_CHECKED: false,
} as const;
